// ============================================================================
// MOCK AUDIT DATA FOR LANDING PAGE
// Dummy data to demonstrate radar chart visualization without authentication
// Based on real audit analysis structure
// ============================================================================

export interface MockCategoryScore {
  name: string;
  score: number;
  question_count: number;
  trend: string;
}

export interface MockDetailedResult {
  question_text: string;
  answer_text: string;
  answer_score: number;
  category: string;
}

export interface MockAuditAnalysis {
  id: number;
  audit_name: string;
  company_name: string;
  overall_score: number;
  analysis_date: string;
  category_scores: MockCategoryScore[];
  detailed_results: MockDetailedResult[];
  prior_year_overall_score?: number;
  prior_year_analysis_date?: string;
  prior_year_category_scores?: MockCategoryScore[];
  prior_year_detailed_results?: MockDetailedResult[];
}

/**
 * Get dynamic dates for current and prior year audits
 * Current: Last month
 * Prior: Same month last year
 */
const getCurrentAuditDate = () => {
  const now = new Date();
  const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 15); // 15th of last month
  return lastMonth.toISOString();
};

const getPriorYearAuditDate = () => {
  const now = new Date();
  const lastMonthPriorYear = new Date(now.getFullYear() - 1, now.getMonth() - 1, 15); // Same month last year
  return lastMonthPriorYear.toISOString();
};

/**
 * Generate last 6 months of dates for KPI tracking
 */
const getLastSixMonths = () => {
  const now = new Date();
  const months = [];
  // Changed from 6 to 12 months to show full year view
  for (let i = 11; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 15);
    months.push(date.toISOString().split('T')[0]);
  }
  return months;
};

/**
 * Helper to generate last 6 months of dates for landing page KPI tracking
 * Returns array of date strings in format "YYYY-MM-DD"
 */
const getLandingPageMonths = () => {
  const now = new Date();
  const months = [];
  // Only 6 months for landing page
  for (let i = 5; i >= 0; i--) {
    const date = new Date(now.getFullYear(), now.getMonth() - i, 15);
    months.push(date.toISOString().split('T')[0]);
  }
  return months;
};

/**
 * Mock review data representing AI Maturity & Everyday Use Review
 * Used for landing page demonstration - no real data or authentication required
 * Categories match the actual AI Maturity & Everyday Use Review Template
 * Includes detailed drill-down data for interactive exploration
 * Includes prior year comparison data
 */
export const mockAuditData: MockAuditAnalysis = {
  id: 999,
  audit_name: 'AI Maturity & Everyday Use Review',
  company_name: 'Tech Innovation Corp',
  overall_score: 68.4,
  analysis_date: getCurrentAuditDate(),
  category_scores: [
    {
      name: 'Vision & Roadmap',
      score: 75.0,
      question_count: 5,
      trend: 'improving',
    },
    {
      name: 'Policy & Compliance',
      score: 82.0,
      question_count: 5,
      trend: 'stable',
    },
    {
      name: 'Process & Performance',
      score: 58.0,
      question_count: 5,
      trend: 'improving',
    },
    {
      name: 'Data Quality & Access',
      score: 65.0,
      question_count: 5,
      trend: 'stable',
    },
    {
      name: 'Platform & Integration',
      score: 62.0,
      question_count: 5,
      trend: 'improving',
    },
  ],
  detailed_results: [
    // Vision & Roadmap (75.0% average)
    {
      question_text: 'Does your organisation have a documented AI strategy aligned with business objectives?',
      answer_text: 'Yes, comprehensive AI strategy with clear roadmap',
      answer_score: 100,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'How frequently is the AI strategy reviewed and updated?',
      answer_text: 'Annually with quarterly progress reviews',
      answer_score: 75,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'Are AI initiatives prioritised based on business impact and feasibility?',
      answer_text: 'Yes, using structured evaluation framework',
      answer_score: 85,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'What percentage of planned AI initiatives are successfully implemented?',
      answer_text: '50-75% of initiatives reach production',
      answer_score: 60,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'Does leadership actively champion AI adoption across the organisation?',
      answer_text: 'Yes, with regular communication and resource allocation',
      answer_score: 85,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },

    // Policy & Compliance (82.0% average)
    {
      question_text: 'Are there formal policies governing AI development and deployment?',
      answer_text: 'Yes, comprehensive policies covering ethics, privacy, and security',
      answer_score: 100,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'How are AI ethics considerations integrated into project workflows?',
      answer_text: 'Mandatory ethics review at each project stage',
      answer_score: 90,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'Is there a process for monitoring AI model bias and fairness?',
      answer_text: 'Regular bias testing with documented mitigation steps',
      answer_score: 80,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'How is data privacy ensured in AI applications?',
      answer_text: 'GDPR-compliant with privacy by design approach',
      answer_score: 85,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'Are AI systems auditable and explainable to stakeholders?',
      answer_text: 'Partially - working on improving model interpretability',
      answer_score: 55,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },

    // Process & Performance (58.0% average)
    {
      question_text: 'How many business processes currently use AI automation?',
      answer_text: '25-50% of processes have some AI integration',
      answer_score: 50,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'Are AI performance metrics tracked and reported regularly?',
      answer_text: 'Yes, monthly KPI reporting on AI ROI',
      answer_score: 75,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'What is the average time from AI model development to production deployment?',
      answer_text: '3-6 months with approval process',
      answer_score: 60,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'How effectively are AI insights actioned by business teams?',
      answer_text: 'Inconsistently - some teams more engaged than others',
      answer_score: 40,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'Is there a feedback loop to continuously improve AI models?',
      answer_text: 'Basic feedback collection, limited systematic improvement',
      answer_score: 65,
      category: 'AI Adoption & Impact - Process & Performance',
    },

    // Data Quality & Access (65.0% average)
    {
      question_text: 'What percentage of business data is accessible for AI/analytics use?',
      answer_text: '50-75% of data is accessible and catalogued',
      answer_score: 65,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'How would you rate the overall quality and consistency of your data?',
      answer_text: 'Good - regular data quality checks with some gaps',
      answer_score: 70,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'Is there a centralised data governance framework in place?',
      answer_text: 'Yes, with defined data stewards and ownership',
      answer_score: 80,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'How quickly can data scientists access data for new AI projects?',
      answer_text: 'Within 1-2 weeks after approval process',
      answer_score: 50,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'Are data lineage and metadata properly documented?',
      answer_text: 'Partially - critical datasets documented, others incomplete',
      answer_score: 60,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },

    // Platform & Integration (62.0% average)
    {
      question_text: 'What AI/ML platforms and tools are currently in use?',
      answer_text: 'Mix of cloud AI services and open-source tools',
      answer_score: 70,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'How well integrated are AI systems with existing business applications?',
      answer_text: 'Moderate integration via APIs, some manual processes remain',
      answer_score: 55,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'Is there a standardised MLOps pipeline for model deployment?',
      answer_text: 'Basic pipeline in place, not fully automated',
      answer_score: 60,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'How scalable is your current AI infrastructure?',
      answer_text: 'Cloud-based with auto-scaling capabilities',
      answer_score: 75,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'Are AI models monitored for performance degradation in production?',
      answer_text: 'Limited monitoring, manual checks monthly',
      answer_score: 50,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
  ],
  
  // Prior year comparison data (dramatic variations - significant improvements and declines)
  prior_year_overall_score: 61.2,
  prior_year_analysis_date: getPriorYearAuditDate(),
  prior_year_category_scores: [
    {
      name: 'Vision & Roadmap',
      score: 48.0, // Significant improvement: 48% → 75% (+27%)
      question_count: 5,
      trend: 'improving',
    },
    {
      name: 'Policy & Compliance',
      score: 90.0, // Declined: 90% → 82% (-8%) - new compliance requirements
      question_count: 5,
      trend: 'declining',
    },
    {
      name: 'Process & Performance',
      score: 38.0, // Major improvement: 38% → 58% (+20%)
      question_count: 5,
      trend: 'improving',
    },
    {
      name: 'Data Quality & Access',
      score: 72.0, // Slight decline: 72% → 65% (-7%)
      question_count: 5,
      trend: 'declining',
    },
    {
      name: 'Platform & Integration',
      score: 58.0, // Minimal improvement: 58% → 62% (+4%)
      question_count: 5,
      trend: 'improving',
    },
  ],
  
  // Prior year detailed results with question-level scores
  prior_year_detailed_results: [
    // Vision & Roadmap (Prior: 48% avg, Current: 75% avg)
    {
      question_text: 'Does your organisation have a documented AI strategy aligned with business objectives?',
      answer_text: 'Partial strategy, not fully documented',
      answer_score: 40,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'How frequently is the AI strategy reviewed and updated?',
      answer_text: 'Annually only, no quarterly reviews',
      answer_score: 50,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'Are AI initiatives prioritised based on business impact and feasibility?',
      answer_text: 'Ad-hoc prioritisation without framework',
      answer_score: 30,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'What percentage of planned AI initiatives are successfully implemented?',
      answer_text: 'Less than 25% reach production',
      answer_score: 25,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },
    {
      question_text: 'Does leadership actively champion AI adoption across the organisation?',
      answer_text: 'Limited leadership involvement',
      answer_score: 45,
      category: 'AI Strategy & Governance - Vision & Roadmap',
    },

    // Policy & Compliance (Prior: 90% avg, Current: 82% avg) - declined
    {
      question_text: 'Are there formal policies governing AI development and deployment?',
      answer_text: 'Yes, comprehensive policies in place',
      answer_score: 100,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'How are AI ethics considerations integrated into project workflows?',
      answer_text: 'Mandatory ethics review at each stage',
      answer_score: 90,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'Is there a process for monitoring AI model bias and fairness?',
      answer_text: 'Comprehensive bias testing',
      answer_score: 95,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'How is data privacy ensured in AI applications?',
      answer_text: 'Full GDPR compliance',
      answer_score: 90,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },
    {
      question_text: 'Are AI systems auditable and explainable to stakeholders?',
      answer_text: 'Fully auditable with strong interpretability',
      answer_score: 75,
      category: 'AI Strategy & Governance - Policy & Compliance',
    },

    // Process & Performance (Prior: 38% avg, Current: 58% avg)
    {
      question_text: 'How many business processes currently use AI automation?',
      answer_text: 'Less than 10% of processes',
      answer_score: 20,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'Are AI performance metrics tracked and reported regularly?',
      answer_text: 'No systematic tracking',
      answer_score: 10,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'What is the average time from AI model development to production deployment?',
      answer_text: 'Over 12 months',
      answer_score: 30,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'How effectively are AI insights actioned by business teams?',
      answer_text: 'Rarely actioned, poor engagement',
      answer_score: 20,
      category: 'AI Adoption & Impact - Process & Performance',
    },
    {
      question_text: 'Is there a feedback loop to continuously improve AI models?',
      answer_text: 'No feedback mechanisms',
      answer_score: 10,
      category: 'AI Adoption & Impact - Process & Performance',
    },

    // Data Quality & Access (Prior: 72% avg, Current: 65% avg) - declined
    {
      question_text: 'What percentage of business data is accessible for AI/analytics use?',
      answer_text: '75%+ of data accessible',
      answer_score: 75,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'How would you rate the overall quality and consistency of your data?',
      answer_text: 'Excellent quality with regular checks',
      answer_score: 80,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'Is there a centralised data governance framework in place?',
      answer_text: 'Yes, comprehensive framework',
      answer_score: 85,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'How quickly can data scientists access data for new AI projects?',
      answer_text: 'Within days',
      answer_score: 70,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },
    {
      question_text: 'Are data lineage and metadata properly documented?',
      answer_text: 'Fully documented for all datasets',
      answer_score: 50,
      category: 'AI Capability & Infrastructure - Data Quality & Access',
    },

    // Platform & Integration (Prior: 58% avg, Current: 62% avg)
    {
      question_text: 'What AI/ML platforms and tools are currently in use?',
      answer_text: 'Basic cloud AI services',
      answer_score: 60,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'How well integrated are AI systems with existing business applications?',
      answer_text: 'Minimal integration',
      answer_score: 45,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'Is there a standardised MLOps pipeline for model deployment?',
      answer_text: 'Manual processes only',
      answer_score: 40,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'How scalable is your current AI infrastructure?',
      answer_text: 'Cloud-based with auto-scaling',
      answer_score: 80,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
    {
      question_text: 'Are AI models monitored for performance degradation in production?',
      answer_text: 'Quarterly manual reviews',
      answer_score: 65,
      category: 'AI Capability & Infrastructure - Platform & Integration',
    },
  ],
};

/**
 * Alternative mock data - Generic Business Performance Audit
 * Can be used to show variety of audit types
 */
export const mockBusinessAudit: MockAuditAnalysis = {
  id: 998,
  audit_name: 'Business Performance & Operations Audit',
  company_name: 'Global Enterprises Ltd',
  overall_score: 71.2,
  analysis_date: '2025-12-15T14:20:00Z',
  category_scores: [
    {
      name: 'Strategic Planning',
      score: 82.0,
      question_count: 3,
      trend: 'improving',
    },
    {
      name: 'Financial Management',
      score: 78.0,
      question_count: 3,
      trend: 'stable',
    },
    {
      name: 'Operational Efficiency',
      score: 65.0,
      question_count: 3,
      trend: 'improving',
    },
    {
      name: 'Customer Experience',
      score: 73.0,
      question_count: 3,
      trend: 'stable',
    },
    {
      name: 'Technology & Innovation',
      score: 68.0,
      question_count: 3,
      trend: 'improving',
    },
    {
      name: 'Risk Management',
      score: 75.0,
      question_count: 3,
      trend: 'stable',
    },
    {
      name: 'Compliance & Governance',
      score: 80.0,
      question_count: 3,
      trend: 'stable',
    },
  ],
  detailed_results: [
    // Strategic Planning
    {
      question_text: 'Does the organisation have a clear 3-5 year strategic plan?',
      answer_text: 'Yes, comprehensive plan reviewed annually',
      answer_score: 90,
      category: 'Strategic Planning',
    },
    {
      question_text: 'How effectively are strategic goals cascaded to operational teams?',
      answer_text: 'Well communicated with quarterly alignment sessions',
      answer_score: 80,
      category: 'Strategic Planning',
    },
    {
      question_text: 'Are market trends and competitive intelligence regularly monitored?',
      answer_text: 'Yes, with monthly reports to leadership',
      answer_score: 75,
      category: 'Strategic Planning',
    },

    // Financial Management
    {
      question_text: 'How accurate are financial forecasts compared to actual results?',
      answer_text: 'Within 5-10% variance consistently',
      answer_score: 75,
      category: 'Financial Management',
    },
    {
      question_text: 'Is there real-time visibility into key financial metrics?',
      answer_text: 'Daily dashboard with automated reporting',
      answer_score: 85,
      category: 'Financial Management',
    },
    {
      question_text: 'How effectively is working capital managed?',
      answer_text: 'Good cash flow management with room for improvement',
      answer_score: 75,
      category: 'Financial Management',
    },

    // Operational Efficiency
    {
      question_text: 'What percentage of processes are documented and standardised?',
      answer_text: '50-75% documented, standardisation in progress',
      answer_score: 60,
      category: 'Operational Efficiency',
    },
    {
      question_text: 'How frequently are operational bottlenecks identified and resolved?',
      answer_text: 'Quarterly reviews with improvement initiatives',
      answer_score: 70,
      category: 'Operational Efficiency',
    },
    {
      question_text: 'Are productivity metrics tracked and used for continuous improvement?',
      answer_text: 'Basic metrics tracked, limited systematic improvement',
      answer_score: 65,
      category: 'Operational Efficiency',
    },

    // Customer Experience
    {
      question_text: 'What is the current Net Promoter Score (NPS) or customer satisfaction rating?',
      answer_text: 'NPS of 45-60 (good, but room for improvement)',
      answer_score: 70,
      category: 'Customer Experience',
    },
    {
      question_text: 'How quickly are customer complaints resolved?',
      answer_text: 'Within 48-72 hours on average',
      answer_score: 75,
      category: 'Customer Experience',
    },
    {
      question_text: 'Is customer feedback systematically collected and acted upon?',
      answer_text: 'Yes, with quarterly improvement initiatives',
      answer_score: 75,
      category: 'Customer Experience',
    },

    // Technology & Innovation
    {
      question_text: 'How modern and integrated are your core business systems?',
      answer_text: 'Mix of modern and legacy systems, integration adequate',
      answer_score: 65,
      category: 'Technology & Innovation',
    },
    {
      question_text: 'What percentage of the budget is allocated to innovation initiatives?',
      answer_text: '5-10% of budget dedicated to innovation',
      answer_score: 70,
      category: 'Technology & Innovation',
    },
    {
      question_text: 'Is there a structured process for evaluating and adopting new technologies?',
      answer_text: 'Yes, with pilot testing and ROI assessment',
      answer_score: 70,
      category: 'Technology & Innovation',
    },

    // Risk Management
    {
      question_text: 'Is there a comprehensive risk register that is regularly updated?',
      answer_text: 'Yes, reviewed quarterly with mitigation plans',
      answer_score: 80,
      category: 'Risk Management',
    },
    {
      question_text: 'How effective is the business continuity planning?',
      answer_text: 'Plans documented and tested annually',
      answer_score: 75,
      category: 'Risk Management',
    },
    {
      question_text: 'Are cybersecurity risks adequately addressed?',
      answer_text: 'Strong security posture with regular assessments',
      answer_score: 70,
      category: 'Risk Management',
    },

    // Compliance & Governance
    {
      question_text: 'Are all relevant regulatory requirements identified and tracked?',
      answer_text: 'Yes, with compliance management system in place',
      answer_score: 85,
      category: 'Compliance & Governance',
    },
    {
      question_text: 'How frequently are compliance audits conducted?',
      answer_text: 'Annual external audits plus quarterly internal reviews',
      answer_score: 80,
      category: 'Compliance & Governance',
    },
    {
      question_text: 'Is there clear accountability and governance structure?',
      answer_text: 'Well-defined roles with board oversight',
      answer_score: 75,
      category: 'Compliance & Governance',
    },
  ],
};

// ============================================================================
// MOCK KPI DATA FOR LANDING PAGE
// Sample KPIs showing traffic light dashboard with trend tracking
// ============================================================================

export interface MockKpiDataPoint {
  date: string;
  value: number;
  target: number;
}

export interface MockKpi {
  id: string;
  name: string;
  category: string;
  unit: string;
  dataPoints: MockKpiDataPoint[];
}

/**
 * Mock KPI data demonstrating traffic light tracking over 12 months (full year)
 * Used for landing page demonstration - shows realistic improvement trends
 */
export const mockKpiData: MockKpi[] = [
  // Financial KPIs
  {
    id: 'gross-profit-margin',
    name: 'Gross Profit Margin',
    category: 'Financial',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 62.0 + (index * 0.7), // Improving trend over 12 months: 62% → 69.7%
      target: 70.0,
    })),
  },
  {
    id: 'operating-expense-ratio',
    name: 'Operating Expense Ratio',
    category: 'Financial',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 38.0 - (index * 0.35), // Improving trend (lower is better): 38% → 34.15%
      target: 32.0,
    })),
  },
  {
    id: 'cash-conversion-cycle',
    name: 'Cash Conversion Cycle',
    category: 'Financial',
    unit: 'days',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 45.0 - (index * 0.9), // Improving trend: 45 → 35.1 days
      target: 30.0,
    })),
  },

  // Operations KPIs
  {
    id: 'order-fulfillment-rate',
    name: 'Order Fulfillment Rate',
    category: 'Operations',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 92.0 + (index * 0.5), // Improving: 92% → 97.5%
      target: 98.0,
    })),
  },
  {
    id: 'inventory-turnover',
    name: 'Inventory Turnover',
    category: 'Operations',
    unit: '',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 6.2 + (index * 0.15), // Improving: 6.2 → 7.85x
      target: 8.0,
    })),
  },
  {
    id: 'defect-rate',
    name: 'Defect Rate',
    category: 'Quality',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 2.5 - (index * 0.07), // Improving (lower is better): 2.5% → 1.73%
      target: 1.5,
    })),
  },
  {
    id: 'on-time-delivery',
    name: 'On-Time Delivery',
    category: 'Operations',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 88.0 + (index * 0.7), // Improving: 88% → 95.7%
      target: 95.0,
    })),
  },

  // Customer KPIs
  {
    id: 'customer-satisfaction',
    name: 'Customer Satisfaction',
    category: 'Customer',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 78.0 + (index * 1.0), // Improving: 78% → 89%
      target: 90.0,
    })),
  },
  {
    id: 'nps-score',
    name: 'Net Promoter Score',
    category: 'Customer',
    unit: '',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 42.0 + (index * 1.5), // Improving: 42 → 58.5
      target: 60.0,
    })),
  },
  {
    id: 'response-time',
    name: 'Avg Response Time',
    category: 'Customer',
    unit: 'hours',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 12.0 - (index * 0.5), // Improving (lower is better): 12h → 6.5h
      target: 6.0,
    })),
  },

  // HR KPIs
  {
    id: 'employee-engagement',
    name: 'Employee Engagement',
    category: 'People',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 72.0 + (index * 0.9), // Improving: 72% → 81.9%
      target: 85.0,
    })),
  },
  {
    id: 'staff-turnover',
    name: 'Staff Turnover Rate',
    category: 'People',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 18.0 - (index * 0.35), // Improving (lower is better): 18% → 14.15%
      target: 12.0,
    })),
  },
  {
    id: 'training-completion',
    name: 'Training Completion',
    category: 'People',
    unit: '%',
    dataPoints: getLastSixMonths().map((date, index) => ({
      date,
      value: 82.0 + (index * 1.1), // Improving: 82% → 94.1%
      target: 95.0,
    })),
  },
];

/**
 * Landing page specific KPI data with only 6 months
 * Used for landing page demonstration with all categories
 */
export const mockKpiDataLandingPage: MockKpi[] = [
  // Financial KPIs
  {
    id: 'gross-profit-margin',
    name: 'Gross Profit Margin',
    category: 'Financial',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 62.0, target: 70.0 }, // Red
      { date: getLandingPageMonths()[1], value: 64.5, target: 70.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 66.0, target: 70.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 68.5, target: 70.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 70.5, target: 70.0 }, // Green!
      { date: getLandingPageMonths()[5], value: 72.0, target: 70.0 }, // Green
    ],
  },
  {
    id: 'operating-expense-ratio',
    name: 'Operating Expense Ratio',
    category: 'Financial',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 36.0, target: 32.0 }, // Red
      { date: getLandingPageMonths()[1], value: 35.5, target: 32.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 34.8, target: 32.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 34.0, target: 32.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 33.5, target: 32.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 33.0, target: 32.0 }, // Amber (still improving but not there yet)
    ],
  },
  {
    id: 'cash-conversion-cycle',
    name: 'Cash Conversion Cycle',
    category: 'Financial',
    unit: 'days',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 38.0, target: 30.0 }, // Red
      { date: getLandingPageMonths()[1], value: 36.5, target: 30.0 }, // Red
      { date: getLandingPageMonths()[2], value: 34.0, target: 30.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 32.0, target: 30.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 31.0, target: 30.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 29.5, target: 30.0 }, // Green!
    ],
  },

  // Operations KPIs
  {
    id: 'order-fulfillment-rate',
    name: 'Order Fulfillment Rate',
    category: 'Operations',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 88.0, target: 98.0 }, // Red
      { date: getLandingPageMonths()[1], value: 90.0, target: 98.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 94.0, target: 98.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 94.0, target: 98.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 96.5, target: 98.0 }, // Amber (close but not quite)
      { date: getLandingPageMonths()[5], value: 96.0, target: 98.0 }, // Amber (slight dip)
    ],
  },
  {
    id: 'inventory-turnover',
    name: 'Inventory Turnover',
    category: 'Operations',
    unit: '',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 6.5, target: 8.0 }, // Amber
      { date: getLandingPageMonths()[1], value: 6.8, target: 8.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 7.2, target: 8.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 7.5, target: 8.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 7.9, target: 8.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 8.2, target: 8.0 }, // Green!
    ],
  },
  {
    id: 'on-time-delivery',
    name: 'On-Time Delivery',
    category: 'Operations',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 88.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[1], value: 90.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 92.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 93.5, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 95.5, target: 95.0 }, // Green!
      { date: getLandingPageMonths()[5], value: 97.0, target: 95.0 }, // Green
    ],
  },

  // Quality KPIs
  {
    id: 'defect-rate',
    name: 'Defect Rate',
    category: 'Quality',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 2.8, target: 1.5 }, // Red
      { date: getLandingPageMonths()[1], value: 2.5, target: 1.5 }, // Red
      { date: getLandingPageMonths()[2], value: 2.2, target: 1.5 }, // Amber
      { date: getLandingPageMonths()[3], value: 1.9, target: 1.5 }, // Amber
      { date: getLandingPageMonths()[4], value: 1.7, target: 1.5 }, // Amber
      { date: getLandingPageMonths()[5], value: 1.5, target: 1.5 }, // Green!
    ],
  },

  // Customer KPIs
  {
    id: 'customer-satisfaction',
    name: 'Customer Satisfaction',
    category: 'Customer',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 78.0, target: 90.0 }, // Red
      { date: getLandingPageMonths()[1], value: 80.0, target: 90.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 82.0, target: 90.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 84.0, target: 90.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 86.0, target: 90.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 88.0, target: 90.0 }, // Amber (improving but not there)
    ],
  },
  {
    id: 'nps-score',
    name: 'Net Promoter Score',
    category: 'Customer',
    unit: '',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 42.0, target: 60.0 }, // Red
      { date: getLandingPageMonths()[1], value: 45.0, target: 60.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 48.0, target: 60.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 51.0, target: 60.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 54.0, target: 60.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 57.0, target: 60.0 }, // Amber (close but steady climb)
    ],
  },
  {
    id: 'response-time',
    name: 'Avg Response Time',
    category: 'Customer',
    unit: 'hours',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 12.0, target: 6.0 }, // Red
      { date: getLandingPageMonths()[1], value: 11.0, target: 6.0 }, // Red
      { date: getLandingPageMonths()[2], value: 10.0, target: 6.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 9.0, target: 6.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 8.0, target: 6.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 7.0, target: 6.0 }, // Amber (improving steadily)
    ],
  },

  // People KPIs
  {
    id: 'employee-engagement',
    name: 'Employee Engagement',
    category: 'People',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 76.0, target: 85.0 }, // Amber
      { date: getLandingPageMonths()[1], value: 78.0, target: 85.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 80.0, target: 85.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 82.0, target: 85.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 84.0, target: 85.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 86.0, target: 85.0 }, // Green!
    ],
  },
  {
    id: 'staff-turnover',
    name: 'Staff Turnover Rate',
    category: 'People',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 15.0, target: 12.0 }, // Amber
      { date: getLandingPageMonths()[1], value: 14.5, target: 12.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 14.0, target: 12.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 13.5, target: 12.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 13.0, target: 12.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 11.8, target: 12.0 }, // Green!
    ],
  },
  {
    id: 'training-completion',
    name: 'Training Completion',
    category: 'People',
    unit: '%',
    dataPoints: [
      { date: getLandingPageMonths()[0], value: 88.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[1], value: 90.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[2], value: 92.0, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[3], value: 93.5, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[4], value: 94.5, target: 95.0 }, // Amber
      { date: getLandingPageMonths()[5], value: 96.0, target: 95.0 }, // Green!
    ],
  },
];

